import { useState, type JSX } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../auth/AuthProvider.js';
import { Layout } from '../components/Layout.js';
import knowledgeAgentGuide from '../help/knowledge-agent.md?raw';
import trainingVideosGuide from '../help/training-videos.md?raw';
import exportsGuide from '../help/exports.md?raw';
import dataExtractorGuide from '../help/data-extractor.md?raw';
import documentTemplatesGuide from '../help/document-templates.md?raw';

/**
 * Help & Documentation hub — employee-facing guides for the Knowledge Agent,
 * Training Videos, exports/branded documents, the Data Extractor, and (admin)
 * document templates. Guides are bundled markdown (frontend/src/help/*.md)
 * rendered with the same ReactMarkdown setup the chat answers use.
 */

interface Guide {
  slug: string;
  title: string;
  summary: string;
  markdown: string;
  /** Shown only to kb_admin — describes admin-only screens other roles can't open. */
  adminOnly?: boolean;
}

const GUIDES: Guide[] = [
  {
    slug: 'knowledge-agent',
    title: 'Knowledge Agent',
    summary: 'Ask questions, attach files, /jira and /qa',
    markdown: knowledgeAgentGuide,
  },
  {
    slug: 'training-videos',
    title: 'Training Videos',
    summary: 'Watch, jump to chapters, ask the trainer',
    markdown: trainingVideosGuide,
  },
  {
    slug: 'exports',
    title: 'Exports & branded documents',
    summary: 'Word, PowerPoint, letterhead, proposal',
    markdown: exportsGuide,
  },
  {
    slug: 'data-extractor',
    title: 'Data Extractor',
    summary: 'Vendor invoices → structured rows',
    markdown: dataExtractorGuide,
  },
  {
    slug: 'document-templates',
    title: 'Document templates (admin)',
    summary: 'Manage letterheads & proposal templates',
    markdown: documentTemplatesGuide,
    adminOnly: true,
  },
];

export function HelpPage(): JSX.Element {
  const { user } = useAuth();
  const isAdmin = user?.role === 'kb_admin';
  const guides = GUIDES.filter((g) => !g.adminOnly || isAdmin);
  const [active, setActive] = useState(guides[0]!.slug);
  const guide = guides.find((g) => g.slug === active) ?? guides[0]!;

  return (
    <Layout>
      <div className="mx-auto flex max-w-5xl flex-col gap-6 px-6 py-8 md:flex-row">
        <nav className="shrink-0 md:w-64">
          <h1 className="ov-h-page mb-4">Help &amp; Guides</h1>
          <ul className="space-y-1">
            {guides.map((g) => (
              <li key={g.slug}>
                <button
                  type="button"
                  onClick={() => setActive(g.slug)}
                  className={`w-full rounded-md px-3 py-2 text-left ${
                    g.slug === guide.slug
                      ? 'bg-nebula-tint font-medium text-nebula-secondary'
                      : 'text-nebula-fg-2 hover:bg-nebula-gray-5'
                  }`}
                >
                  <span className="block text-sm">{g.title}</span>
                  <span className="block text-xs text-nebula-fg-3">{g.summary}</span>
                </button>
              </li>
            ))}
          </ul>
        </nav>
        <article className="prose prose-sm min-w-0 max-w-none flex-1">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{guide.markdown}</ReactMarkdown>
        </article>
      </div>
    </Layout>
  );
}
